import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import * as THREE from "three";
import {
  ArrowRight,
  Sparkles,
  Video,
  LayoutGrid,
  Maximize2,
  Layers,
  Gift,
  MousePointerClick,
  Check,
  Globe2,
  TrendingUp,
  Trophy,
} from "lucide-react";

function MangaCanvas() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = mount.clientWidth;
    const h = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(70, w / h, 0.1, 100);
    camera.position.z = 9;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // Flowing torus knots — manga "ink ribbon" vibe
    const knot1 = new THREE.Mesh(
      new THREE.TorusKnotGeometry(2.4, 0.4, 100, 14),
      new THREE.MeshBasicMaterial({
        color: 0x8a2be2,
        wireframe: true,
        transparent: true,
        opacity: 0.35,
      }),
    );
    const knot2 = new THREE.Mesh(
      new THREE.TorusKnotGeometry(1.6, 0.25, 80, 12, 3, 5),
      new THREE.MeshBasicMaterial({
        color: 0x00f0ff,
        wireframe: true,
        transparent: true,
        opacity: 0.25,
      }),
    );
    scene.add(knot1, knot2);

    // Confetti points
    const count = 900;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 22;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 14;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 22;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const points = new THREE.Points(
      geom,
      new THREE.PointsMaterial({
        size: 0.04,
        color: 0xffffff,
        transparent: true,
        opacity: 0.55,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    scene.add(points);

    let raf;
    const animate = (t) => {
      const time = t * 0.0005;
      knot1.rotation.x = time * 0.3;
      knot1.rotation.y = time * 0.4;
      knot2.rotation.x = -time * 0.35;
      knot2.rotation.z = time * 0.25;
      points.rotation.y = time * 0.1;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const onResize = () => {
      const w2 = mount.clientWidth;
      const h2 = mount.clientHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      knot1.geometry.dispose();
      knot1.material.dispose();
      knot2.geometry.dispose();
      knot2.material.dispose();
      geom.dispose();
      points.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      aria-hidden
      className="absolute inset-0 -z-0"
      style={{ pointerEvents: "none" }}
    />
  );
}

const formats = [
  { icon: Video, name: "Video Ads", desc: "Pre-roll, mid-roll, in-content" },
  { icon: LayoutGrid, name: "Display Ads", desc: "High-impact visual formats" },
  { icon: Maximize2, name: "Interstitials", desc: "Full-screen engagement" },
  { icon: Layers, name: "Banners", desc: "Strategic placement" },
  { icon: Gift, name: "Rewarded Ads", desc: "Incentivized engagement" },
  { icon: MousePointerClick, name: "Pop-Up/Under", desc: "Strategic user moments" },
];

const markets = [
  {
    flag: "🇯🇵",
    name: "Japan",
    size: "$3.3B",
    growth: "+15%",
    desc: "Digital manga sales dominating e-book market",
    gradient: "from-[#FF3E6C]/20 to-[#0047FF]/20",
  },
  {
    flag: "🇺🇸",
    name: "United States",
    size: "$1.1B",
    growth: "+22%",
    desc: "Rapidly growing mobile-first audience",
    gradient: "from-[#0047FF]/20 to-[#00F0FF]/20",
  },
  {
    flag: "🇨🇳",
    name: "China",
    size: "$3.1B",
    growth: "+28%",
    desc: "Smartphone-focused consumption surge",
    gradient: "from-[#8A2BE2]/20 to-[#00F0FF]/20",
  },
];

export default function Manga() {
  return (
    <main className="relative pt-32 pb-12">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <MangaCanvas />
        <div className="aurora-orb bg-[#8A2BE2] w-[600px] h-[600px] -top-40 -left-40 opacity-50" />
        <div className="aurora-orb bg-[#00F0FF] w-[500px] h-[500px] top-40 right-0 opacity-30" />

        <div className="container-x relative z-10 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs uppercase tracking-[0.2em] mb-8"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
              Trusted by 100+ Publishers · 3B+ Monthly Ad Requests
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-[clamp(2.6rem,7vw,6rem)] font-bold mb-6"
            >
              Monetize Every
              <br />
              <span className="aurora-text">Manga Audience</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="text-lg md:text-xl text-white/65 max-w-xl mb-10"
            >
              The world's first AI-powered monetization platform designed
              specifically for manga creators and publishers. Turn your passion
              into profit with intelligent ad optimization.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="flex flex-wrap items-center gap-4"
            >
              <a
                href="/signup"
                target="_blank"
                rel="noreferrer"
                data-testid="manga-cta-start"
                className="btn-primary"
              >
                Start Earning Now
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="mailto:hello@monetiseup.com?subject=Manga%20Demo"
                className="btn-ghost"
              >
                Schedule a Demo
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="grid grid-cols-3 gap-6 mt-16 max-w-md"
            >
              {[
                { v: 50, suffix: "+", label: "Manga Publishers" },
                { v: 5, prefix: "$", suffix: "M+", label: "Revenue Processed" },
                { v: 180, suffix: "+", label: "Countries" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="font-display text-3xl md:text-4xl font-bold">
                    <CountUp
                      end={s.v}
                      duration={2.2}
                      prefix={s.prefix || ""}
                      suffix={s.suffix || ""}
                    />
                  </div>
                  <div className="text-xs text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Revenue card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="relative float-slow"
          >
            <div className="glass-strong rounded-3xl p-7">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-[#00F0FF]" />
                <span className="text-sm font-medium">Revenue Growth</span>
              </div>
              <p className="text-xs text-white/45 mb-7">
                See how publishers grow with MonetiseUP
              </p>

              <div className="font-display text-6xl font-bold aurora-text mb-1">
                +141%
              </div>
              <div className="text-xs text-white/50 mb-7">Revenue Growth</div>

              <div className="grid grid-cols-2 gap-6 pt-6 border-t border-white/5">
                <div>
                  <div className="font-display text-2xl font-bold">$15.2</div>
                  <div className="text-xs text-white/50">Average CPM</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold">3B+</div>
                  <div className="text-xs text-white/50">Impressions/Month</div>
                </div>
              </div>

              <div className="mt-5 text-xs text-emerald-400 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Notable results within the first month
              </div>
            </div>
            <div className="absolute -inset-3 -z-10 rounded-3xl bg-gradient-to-br from-[#8A2BE2]/30 to-[#0047FF]/20 blur-2xl" />
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section className="relative py-32">
        <div className="container-x">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
                About MonetiseUP
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-[1.05]">
                The only ad network built
                <br />
                <span className="aurora-text">for manga & anime</span>
              </h2>
              <p className="text-white/65 leading-relaxed">
                MonetiseUP is the only advertising network specifically crafted
                for the Manga, Anime, and Streaming content ecosystem. Founded
                in 2024, our platform is built on data-driven innovation,
                trusted by over 100 publishers worldwide, and processing billions
                of ad requests monthly.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="tile p-7">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 grid place-items-center mb-4 border border-white/10">
                  <Sparkles className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">
                  Data-Driven Innovation
                </h3>
                <p className="text-sm text-white/65">
                  Built on cutting-edge technology and deep understanding of
                  manga and anime audience behavior patterns.
                </p>
              </div>
              <div className="tile p-7">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8A2BE2]/30 to-[#00F0FF]/20 grid place-items-center mb-4 border border-white/10">
                  <Globe2 className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">
                  Global Trust
                </h3>
                <p className="text-sm text-white/65">
                  Serving publishers worldwide with transparent reporting and
                  reliable payment systems since 2024.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="relative py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">
              Why Choose MonetiseUP?
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold">
              The difference is{" "}
              <span className="aurora-text">specialization</span>
            </h2>
            <p className="text-white/60 mt-4">
              Experience the difference with our specialized approach to manga
              and anime monetization.
            </p>
          </div>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mb-6">
            <div className="tile p-8 md:p-10">
              <div className="text-xs uppercase tracking-[0.2em] text-[#00F0FF] mb-3">
                Manga-First Monetisation Services
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold mb-3">
                Bespoke solutions for{" "}
                <span className="cyan-text">unique consumption patterns</span>
              </h3>
              <p className="text-white/65">
                We are the only ad network offering bespoke monetization
                solutions tailored to the unique consumption patterns of Manga
                and Anime fans.
              </p>
            </div>
            <div className="glass-strong rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 inline-flex items-center gap-1.5 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live Performance
                </div>
                <div className="font-display text-5xl font-bold aurora-text">
                  +141%
                </div>
                <div className="text-sm text-white/60 mt-1">
                  Average Revenue Increase
                </div>
              </div>
              <div className="mt-6 grid grid-cols-7 gap-1.5 h-16 items-end">
                {[35, 50, 40, 65, 55, 80, 95].map((h, i) => (
                  <div
                    key={i}
                    className="rounded-t bg-gradient-to-t from-[#8A2BE2]/40 to-[#00F0FF]"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="tile p-8">
              <h3 className="font-display text-2xl font-bold mb-3">
                Exceptional <span className="cyan-text">Earn Rates</span>
              </h3>
              <p className="text-white/65">
                Publishers see up to 141% higher revenues, all while preserving
                user satisfaction and on-site experiences.
              </p>
            </div>
            <div className="tile p-8">
              <h3 className="font-display text-2xl font-bold mb-3">
                UX-Conscious <span className="cyan-text">Monetisation</span>
              </h3>
              <p className="text-white/65">
                Our formats are designed to engage—not annoy—ensuring ads feel
                native and enhance the experience, not detract from it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Formats */}
      <section className="relative py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">
              Product Offering
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold">
              Our Ad Formats &{" "}
              <span className="aurora-text">Product Offering</span>
            </h2>
            <p className="text-white/60 mt-4">
              A complete suite of ad formats engineered for high performance and
              aesthetic harmony.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {formats.map((f) => (
              <div key={f.name} className="tile p-7">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 grid place-items-center mb-4 border border-white/10">
                  <f.icon className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="font-display text-lg font-bold mb-1">
                  {f.name}
                </h3>
                <p className="text-sm text-white/60">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="glass-strong rounded-3xl p-8 md:p-10">
            <h3 className="font-display text-2xl font-bold mb-6">
              Key <span className="cyan-text">Highlights</span>
            </h3>
            <ul className="grid md:grid-cols-2 gap-4">
              {[
                "High-CPM revenue generation",
                "Seamless integration with Manga/Anime platforms",
                "Maintained UI/UX quality",
                "Broad coverage across all major publisher touchpoints",
              ].map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-sm text-white/80"
                >
                  <Check className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Market opportunity */}
      <section className="relative py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">
              The Market Opportunity
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold">
              Manga & streaming markets{" "}
              <span className="aurora-text">are exploding</span>
            </h2>
            <p className="text-white/60 mt-4">
              Massive market growth driven by mobile-native audiences and
              digital consumption patterns.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {markets.map((m) => (
              <div
                key={m.name}
                className={`relative rounded-3xl p-8 border border-white/10 bg-gradient-to-br ${m.gradient} backdrop-blur-xl`}
              >
                <div className="text-5xl mb-4">{m.flag}</div>
                <h3 className="font-display text-2xl font-bold mb-2">
                  {m.name}
                </h3>
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-display text-4xl font-bold">
                    {m.size}
                  </span>
                  <span className="text-emerald-400 text-sm font-mono">
                    {m.growth}
                  </span>
                </div>
                <p className="text-sm text-white/65">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success stories */}
      <section className="relative py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">
              Case Studies
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold">
              Real <span className="aurora-text">Success Stories</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="tile p-8">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-[#00F0FF]" />
                <span className="text-xs uppercase tracking-[0.2em] text-white/50">
                  Manga Publisher Success
                </span>
              </div>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-display text-5xl font-bold aurora-text">
                  $300K
                </span>
                <span className="text-white/60">in Annual Revenue</span>
              </div>
              <p className="text-white/65">
                Using tailored ad strategies and high-CPM formats.
              </p>
            </div>
            <div className="tile p-8">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-[#00F0FF]" />
                <span className="text-xs uppercase tracking-[0.2em] text-white/50">
                  Vietnam Fansite
                </span>
              </div>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-display text-5xl font-bold aurora-text">
                  $6.66K
                </span>
                <span className="text-white/60">in 7 Weeks</span>
              </div>
              <p className="text-white/65">
                3M impressions with smart placement and UX integration.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto text-center">
            <div className="tile p-6">
              <div className="font-display text-2xl font-bold">Founded 2024</div>
            </div>
            <div className="tile p-6">
              <div className="font-display text-2xl font-bold">
                100+ Publishers
              </div>
            </div>
            <div className="tile p-6">
              <div className="font-display text-2xl font-bold">
                3B+ Monthly Requests
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-[2.5rem] glass-strong p-10 md:p-16">
            <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-gradient-to-br from-[#8A2BE2]/30 to-[#00F0FF]/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-3xl">
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-5">
                Ready to Unlock Full{" "}
                <span className="aurora-text">Monetisation Potential?</span>
              </h2>
              <p className="text-white/70 text-lg mb-8">
                Join 100+ publishers who have transformed their revenue with
                MonetiseUP's specialized manga and anime monetization platform.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <a
                  href="mailto:hello@monetiseup.com?subject=Schedule%20Manga%20Demo"
                  className="btn-primary"
                >
                  Schedule Your Demo
                </a>
                <a
                  href="/signup"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                >
                  Start Monetizing Today
                </a>
              </div>

              <div className="grid grid-cols-3 gap-6 max-w-md">
                <div>
                  <div className="font-display text-3xl font-bold">24/7</div>
                  <div className="text-xs text-white/55">Expert Support</div>
                </div>
                <div>
                  <div className="font-display text-3xl font-bold">99.9%</div>
                  <div className="text-xs text-white/55">Uptime Guaranteed</div>
                </div>
                <div>
                  <div className="font-display text-3xl font-bold">0</div>
                  <div className="text-xs text-white/55">Setup Fees</div>
                </div>
              </div>

              <p className="text-xs text-white/40 mt-10">
                Questions? Contact our specialists at{" "}
                <a
                  href="mailto:hello@monetiseup.com"
                  className="text-[#00F0FF] hover:underline"
                >
                  hello@monetiseup.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
